
import React, { useState } from 'react';
import { TwitterDeliverable } from '@/types/twitterTypes';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Calendar, RefreshCw, AlertCircle, Search, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import DeliverableTweetsView from './DeliverableTweetsView';
import KolAvatarGroup from './KolAvatarGroup';
import { useTwitterJobStatus } from '@/hooks/use-twitter-job-status';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ExpandableDeliverableProps {
  deliverable: TwitterDeliverable;
}

const ExpandableDeliverable: React.FC<ExpandableDeliverableProps> = ({ deliverable }) => {
  const [expanded, setExpanded] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Pass deliverable.id (the specific ID being clicked) to useTwitterJobStatus
  const { 
    jobStatus, 
    processingProgress, 
    startBackgroundProcessing,
    performDirectSearch,
    cancelProcessing
  } = useTwitterJobStatus(deliverable.id);

  const getStatusBadge = () => {
    if (jobStatus === 'processing') {
      return (
        <Badge variant="secondary" className="bg-amber-500 hover:bg-amber-600 text-xs">
          Processing ({processingProgress}%)
        </Badge>
      );
    }
    
    if (jobStatus === 'completed') {
      return <Badge variant="secondary" className="bg-green-500 hover:bg-green-600 text-xs">Completed</Badge>;
    }
    
    if (jobStatus === 'failed') {
      return <Badge variant="destructive" className="text-xs">Failed</Badge>;
    }
    
    return <Badge variant="outline" className="text-xs">Not Started</Badge>;
  };

  const handleStartBackgroundProcessing = async () => {
    try {
      setProcessingError(null);
      console.log(`Starting background processing for deliverable: ${deliverable.id}`);
      const success = await startBackgroundProcessing();
      
      if (!success) {
        setProcessingError('Failed to start background processing. Check edge function logs.');
        toast({
          title: 'Processing Error',
          description: 'Failed to start background processing. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Error starting background processing:', err);
      setProcessingError(err.message || 'Unknown error occurred');
      toast({
        title: 'Processing Error',
        description: err.message || 'Failed to start background processing',
        variant: 'destructive',
      });
    }
  };

  const handleDirectSearch = async () => {
    try {
      setProcessingError(null);
      console.log(`Performing direct search for deliverable: ${deliverable.id}`);
      const success = await performDirectSearch();
      
      if (!success) {
        setProcessingError('Direct search failed. Check edge function logs.');
        toast({
          title: 'Search Error',
          description: 'Direct search failed. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Error performing direct search:', err);
      setProcessingError(err.message || 'Unknown error occurred');
      toast({
        title: 'Search Error',
        description: err.message || 'Direct search failed',
        variant: 'destructive',
      });
    }
  };
  
  const handleResetDeliverable = async () => {
    try {
      console.log(`Resetting deliverable: ${deliverable.id}`);
      setProcessingError(null);
      
      // Update the deliverable status in the database
      const { error } = await supabase
        .from('twitter_deliverables')
        .update({
          job_status: 'none',
          processing_progress: 0,
          processing_details: null,
          error_message: null,
          last_fetched: null
        })
        .eq('id', deliverable.id);

      if (error) throw error;
      
      // Delete all tweets for this deliverable
      const { error: deleteError } = await supabase
        .from('twitter_search_results')
        .delete()
        .eq('deliverable_id', deliverable.id);
        
      if (deleteError) throw deleteError;
      
      toast({
        title: 'Deliverable reset',
        description: 'All tweets have been deleted and processing status has been reset.',
      });
      
    } catch (err: any) {
      console.error('Error resetting deliverable:', err);
      setProcessingError(err.message || 'Unknown error occurred');
      toast({
        title: 'Error',
        description: 'Failed to reset deliverable.',
        variant: 'destructive',
      });
    }
  };

  // Parse the content query to highlight OR operators
  const renderContentQuery = () => {
    if (!deliverable.content_query) return "No query";

    return deliverable.content_query.split(/(\s+OR\s+)/g).map((part, index) => {
      if (part.trim() === 'OR') {
        return (
          <Badge key={index} variant="primary" className="mx-1">
            {part}
          </Badge>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Transform kol_usernames array into the format expected by KolAvatarGroup
  const transformKolUsersForAvatarGroup = () => {
    if (!deliverable.kol_usernames || !Array.isArray(deliverable.kol_usernames)) {
      return [];
    }
    
    return deliverable.kol_usernames.map(username => ({
      name: username, // Using username as name since we don't have full name data
      image: undefined // No image data available
    }));
  };

  return (
    <Card className="overflow-hidden transition-all border-primary/20 border">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm">{deliverable.name}</div>
            {getStatusBadge()}
          </div>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </CardHeader>
      
      <CardContent className="pb-3">
        <div className="space-y-4 text-xs"> 
          <div>
            <div className="text-sm font-medium mb-2">Content Query:</div>
            <div className="text-xs p-2 bg-muted/30 rounded">
              {renderContentQuery()}
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center text-xs text-muted-foreground gap-2">
              <Calendar className="h-3 w-3" />
              <span>
                {deliverable.start_date && deliverable.end_date 
                  ? `${format(new Date(deliverable.start_date), 'MMM d, yyyy')} - ${format(new Date(deliverable.end_date), 'MMM d, yyyy')}` 
                  : 'No date range'}
              </span>
            </div>
          </div>
          
          <div>
            <div className="text-xs font-medium mb-1">KOLs:</div>
            <KolAvatarGroup users={transformKolUsersForAvatarGroup()} limit={10} />
          </div>
        </div>
      </CardContent>
      
      {processingError && (
        <div className="px-6 mb-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Processing Error</AlertTitle>
            <AlertDescription>
              {processingError}
            </AlertDescription>
          </Alert>
        </div>
      )}
      
      <CardFooter className="border-t pt-3 pb-3 flex justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="h-8 text-xs"
            onClick={handleDirectSearch}
            disabled={jobStatus === 'processing'}
          >
            <Search className="h-3 w-3 mr-1" />
            Direct Search
          </Button>
          
          <Button 
            variant={jobStatus === 'processing' ? "destructive" : "default"}
            size="sm"
            className="h-8 text-xs"
            onClick={jobStatus === 'processing' ? cancelProcessing : handleStartBackgroundProcessing}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${jobStatus === 'processing' ? 'animate-spin' : ''}`} />
            {jobStatus === 'processing' ? 'Cancel Processing' : 'Background Processing'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
            onClick={handleResetDeliverable}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
        
        <Button 
          variant="secondary" 
          size="sm"
          className="h-8 text-xs"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide Results' : 'View Results'}
          {expanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
        </Button>
      </CardFooter>
      
      {/* Expanded section with tweets */}
      {expanded && (
        <div className="border-t p-4">
          {jobStatus === 'failed' && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Processing Failed</AlertTitle>
              <AlertDescription>
                There was an error retrieving tweets. Please try again.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Pass the deliverable ID explicitly from this component */}
          <DeliverableTweetsView deliverableId={deliverable.id} />
        </div>
      )}
    </Card>
  );
};

export default ExpandableDeliverable;
